import { ContextType, useContext, useEffect, useRef } from 'react';
import { Navigator as BaseNavigator, UNSAFE_NavigationContext as NavigationContext } from 'react-router-dom';
import { Blocker, History, Transition } from 'history';
import { useUnmount } from 'src/hooks';

interface Navigator extends BaseNavigator {
  block: History['block'];
}

type NavigationContextWithBlock = ContextType<typeof NavigationContext> & {
  navigator: Navigator;
};

/**
 * @source https://github.com/remix-run/react-router/commit/256cad70d3fd4500b1abcfea66f3ee622fb90874
 */
export function useBlocker(isDirty: boolean, blocker: Blocker) {
  const { navigator } = useContext(NavigationContext) as NavigationContextWithBlock;
  // main tweak required to OP was wrapping unblock in ref so we're only pushing one blocker
  // on the stack per each useBlocker hook declaration (i.e. not one for every render)
  const refUnBlock = useRef<() => void>();

  useEffect(() => {
    if (!isDirty) {
      refUnBlock.current?.();
      return;
    }

    refUnBlock.current = navigator.block((tx: Transition) => {
      const autoUnblockingTx = {
        ...tx,
        retry() {
          refUnBlock.current?.(); // need to unblock so retry succeeds
          tx.retry();
        }
      };

      blocker(autoUnblockingTx);
    });
  }, [navigator, blocker, isDirty]);

  useUnmount(() => {
    refUnBlock.current?.();
  });
}
